/**
 * Renders its children only once the surface manifests have loaded.
 *
 * A page that attributes records to surfaces cannot do it correctly with an
 * empty registry: every alert would fall into "Other", and a module filter
 * built from nothing would widen to every module. So the page waits, and if
 * the registry cannot be loaded it says so, with the same error state every
 * Atlas page uses when a backend is down.
 *
 *   <SurfacesGate>{(lists) => <Page moduleRoutes={lists.MODULE_ROUTES} />}</SurfacesGate>
 */
import React from 'react';
import { ErrorState, Loading } from '../../pages/security-atlas/_shared/components';
import { useSurfaces } from './useSurfaces';

const SurfacesGate = ({ children }) => {
  const { status, lists, surfaces, retry } = useSurfaces();
  if (status === 'error') return <ErrorState service="Agent Atlas surface registry" onRetry={retry} />;
  if (status !== 'ready') return <Loading />;
  return children(lists, surfaces);
};

/**
 * Wrap a page so it receives the derived lists as `surfaceLists`, once they
 * have loaded. The page's own props pass through untouched, so a page mounted
 * with props elsewhere (a pinned embed, a view prop) keeps working.
 */
export const withSurfaces = (Page) => {
  const WithSurfaces = (props) => (
    <SurfacesGate>{(lists) => <Page {...props} surfaceLists={lists} />}</SurfacesGate>
  );
  WithSurfaces.displayName = `withSurfaces(${Page.displayName || Page.name || 'Page'})`;
  return WithSurfaces;
};

export default SurfacesGate;
